/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Form, Field } from 'react-final-form'
import { Helmet } from 'react-helmet'

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../states/containers/store'
import { createLogin, fetchAccessToken, fetchMfaToken } from '../../states/containers/auth/loginSlice'
import { Login, LoginResponse } from '../../states/types/auth'

import {
  createMicrosoftSync,
  createMicrosoftZendesk,
  MicrosoftSyncState,
} from '../../states/containers/auth/MicrosoftSyncSlice'
import {
  createGoogleSync,
  createGoogleZendesk,
  GoogleSyncState,
} from '../../states/containers/auth/googleSyncSlice'

import { URL } from '../constants/url'
import { setPath, getGetPrams, openNewTab } from '../../utils/url'
import Loading from '../components/common/Loading'
import { isManager, isMember } from '../../utils/role'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getZendeskToken } from '../../states/apis/auth'
import { setErrors } from '../../states/containers/common/errorsSlice'
import { fetchProfile } from '../../states/containers/setting/profileSlice'
import { fetchReportList } from '../../states/containers/report/reportSlice'
import { nowDate, prevMonth } from '../../utils/date'

import '../assets/scss/login.scss'

const Login: FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t, i18n } = useTranslation()

  const {
    errors,
  } = useSelector((state: RootState) => state)

  const [initLang, setInitLang] = useState('ja')
  const [canView, setCanView] = useState<boolean>(false)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)
  const [isSSOError, setIsSSOError] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isMfa, setIsMfa] = useState<boolean>(false)
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")

  interface GetParams {
    redirect_path: string
    deny_sso: boolean
    zendesk: boolean
    hidden: boolean
  }

  const initialLoginValue = {
    lang: 'ja',
  }

  const redirectToTopPage = (lang: string, login?: LoginResponse) => {
    history.push(setPath(URL.TOP.path, lang))
  }

  const redirectToPath = (path: string) => history.push(path)

  const redirect = (lang: string, login?: LoginResponse) => {
    const searchParams = location.search
    if (!searchParams) {
      redirectToTopPage(lang, login)
    } else {
      const getParams = getGetPrams(searchParams) as GetParams
      if (getParams.zendesk) {
        getZendeskToken()
          .then((res) => {
            openNewTab(res.url, 'post')
          })
          .catch((error) => {
            dispatch(setErrors(error))
          })
      } else if (!getParams.zendesk && getParams.redirect_path) {
        redirectToPath(getParams.redirect_path)
      } else {
        redirectToTopPage(lang, login)
      }
    }
  }

  useEffect(() => {
    let browserLangValue = (navigator.languages && navigator.languages[0]) || navigator.language
    if (browserLangValue.match(/en/)) {
      browserLangValue = 'en'
    } else {
      browserLangValue = 'ja'
    }
    const getParams = getGetPrams(location.search) as GetParams
    dispatch(fetchAccessToken(
      (result) => {
        dispatch(fetchProfile())
        redirect(browserLangValue, result)
      },
      () => {
        setInitLang(browserLangValue)
        if (getParams.hidden) {
          setCanView(false)
        } else {
          setCanView(true)
        }
      },
    ))
    if (getParams.deny_sso) {
      setIsSSOError(getParams.deny_sso)
    }
    const targetElements = Array.from(document.getElementsByClassName('modal-backdrop'))
    targetElements.map((value) => value.remove())
  }, [])

  const getZendesk = () => {
    getZendeskToken()
      .then((res) => {
        openNewTab(res.url, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  const handleSubmit = (values: Login) => {
    const getParams = getGetPrams(location.search) as GetParams
    setIsSubmit(true)
    dispatch(
      createLogin(values, (result) => {
        if (!result.token) {
          setIsMfa(true)
          setEmail(values.email)
          setPassword(values.password)
        } else if (getParams.zendesk) {
          getZendesk()
        } else {
          dispatch(fetchProfile())
          if (isManager(result.member.role)) {
            dispatch(fetchReportList({ target_date: Number(nowDate("DD")) <= 8 ? prevMonth(nowDate(), "YYYY-MM-01") : nowDate("YYYY-MM-01") }))
          }
          redirect(initLang, result)
          setIsSubmit(false)
        }
      }, () => setIsSubmit(false)),
    )
  }

  const handleMfaAuth = (values: Login) => {
    setIsSubmit(true)
    setIsError(false)
    const getParams = getGetPrams(location.search) as GetParams
    dispatch(
      fetchMfaToken(values, (result) => {
        if (getParams.zendesk) {
          getZendesk()
        } else {
          dispatch(fetchProfile())
          if (isManager(result.member.role)) {
            dispatch(fetchReportList({ target_date: Number(nowDate("DD")) <= 8 ? prevMonth(nowDate(), "YYYY-MM-01") : nowDate("YYYY-MM-01") }))
          }
          redirect(initLang, result)
        }
      }, () => {
        setIsSubmit(false)
        setIsError(true)
      }),
    )
  }

  const handleMicrosoftSync = () => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.zendesk) {
      dispatch(
        createMicrosoftZendesk((microsoft: MicrosoftSyncState) => {
          location.href = microsoft.login_url
        }),
      )
    } else {
      dispatch(
        createMicrosoftSync((microsoft: MicrosoftSyncState) => {
          location.href = microsoft.login_url
        }),
      )
    }
  }

  const handleGoogleSync = () => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.zendesk) {
      dispatch(
        createGoogleZendesk((google: GoogleSyncState) => {
          location.href = google.login_url
        }),
      )
    } else {
      dispatch(
        createGoogleSync((google: GoogleSyncState) => {
          location.href = google.login_url
        }),
      )
    }
  }

  useEffect(() => {
    i18n.changeLanguage(initLang)
  }, [initLang, i18n])

  useEffect(() => {
    document.getElementsByTagName('html')[0].classList.remove('modal_open')
  }, [])

  const DefaultDisp = (
    <>
      <div className="sign_in_title">
        {t('login.title')}
      </div>
      <div className="login-box-body">
        <Form
          onSubmit={handleSubmit}
          initialValues={initialLoginValue}
          // eslint-disable-next-line no-shadow
          render={({ handleSubmit }) => (
            <>
              <form onSubmit={handleSubmit}>
                <div className="input_area">
                  <div className="form-group">
                    <p>
                      {t('login.mail')}
                    </p>
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      component="input"
                      className="form-control"
                      placeholder="Email"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <p>
                      {t('login.password')}
                    </p>
                    <Field
                      type="password"
                      minLength="8"
                      maxLength="64"
                      name="password"
                      id="password"
                      component="input"
                      className="form-control"
                      placeholder="Password"
                      required
                    />
                  </div>
                </div>
                {
                  errors.formErrors.form && (
                    // eslint-disable-next-line react/no-danger
                    <p className="form-error" dangerouslySetInnerHTML={{ __html: t('login.error.text') }} />
                  )
                }
                {
                  isSubmit ? (
                    <Loading />
                  ) : (
                    <button
                      name="button"
                      type="submit"
                      className="btn btn-primary btn-block btn-flat login_button"
                      disabled={isSubmit}
                    >
                      {t('login.button.login')}
                    </button>
                  )
                }
              </form>
              <div style={{ textAlign: "center" }}>
                <a href="/login/reset_password">
                  {t('login.forgot_password')}
                </a>
              </div>
              <hr />
              <button
                type="button"
                className="btn sso-google btn-block btn-flat"
                onClick={handleGoogleSync}
              >
                <img src="/static/images/logo_google.svg" alt="Google" />
                {t('login.button.google')}
              </button>
              <button
                type="button"
                className="btn sso-microsoft btn-block btn-flat"
                onClick={handleMicrosoftSync}
              >
                <img src="/static/images/logo_microsoft.svg" alt="Microsoft" />
                {t('login.button.microsoft')}
              </button>
              <div className="login_info">
                <div className="textarea" data-html="true" data-tip={t('login.info.description')}>
                  <span>{t('login.info.title')}</span>
                  <FontAwesomeIcon icon={['far', 'question-circle']} />
                  <ReactTooltip place="bottom" effect="solid" />
                </div>
              </div>
            </>
          )}
        />
      </div>
    </>
  )

  const MfaDisp = (
    <>
      <div className="sign_in_title">
        {t('login.mfa.title')}
      </div>
      <div className="login-box-body">
        <Form
          onSubmit={handleMfaAuth}
          initialValues={initialLoginValue}
          // eslint-disable-next-line no-shadow
          render={({ handleSubmit }) => (
            <>
              <form onSubmit={handleSubmit}>
                <div className="input_area">
                  <div className="form-group">
                    <p>
                      {t('login.mfa.code')}
                    </p>
                    <Field
                      type="text"
                      minLength="0"
                      maxLength="6"
                      name="mfa_code"
                      id="mfa_code"
                      component="input"
                      className="form-control"
                      placeholder={t('login.mfa.place_holder')}
                      required
                    />
                    <Field
                      type="hidden"
                      name="email"
                      id="email"
                      component="input"
                      value={email}
                    />
                    <Field
                      type="hidden"
                      name="password"
                      id="password"
                      component="input"
                      value={password}
                    />
                  </div>
                  {
                    isError && (
                      <div className="form-error">
                        {t('login.mfa.error')}
                      </div>
                    )
                  }
                </div>
                <button
                  name="button"
                  type="submit"
                  className="btn btn-primary btn-block btn-flat login_button"
                >
                  {t('login.mfa.button')}
                </button>
              </form>
            </>
          )}
        />
      </div>
    </>
  )

  return (
    <>
      {
        canView ? (
          <>
            <Helmet>
              <title>Qasee - login</title>
            </Helmet>

            <div className="login_wrapper">
              <div className="login-logo">
                <img
                  className="login-logo"
                  src="/static/images/logo-login.png"
                  alt="Logo login"
                />
              </div>
              {
                isMfa ? (
                  MfaDisp
                ) : (
                  DefaultDisp
                )
              }
            </div>
            {
              isSSOError && (
                <div className="sso_error">
                  <p className="error_title">
                    <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
                    <span>{t('login.error.sso.title')}</span>
                  </p>
                  <p>{t('login.error.sso.description')}</p>
                  <button
                    type="button"
                    onClick={() => setIsSSOError(false)}
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </button>
                </div>
              )
            }
          </>
        ) : (
          <>
            <Loading />
          </>
        )
      }
    </>
  )
}

export default Login
